import React, { useEffect, useMemo, useState } from "react";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { ArrowUp, ArrowDown, ArrowUpDown, Download } from "lucide-react";
import EventLeaderboardShareCard from "@/components/EventLeaderboardShareCard";
import useDownloadEventLeaderboard from "@/lib/useDownloadEventLeaderboard";
import { LeaderboardSearchBar, matchesLeaderboardFilters } from "@/components/LeaderboardSearchBar";
import { PODIUM } from "@/lib/podium";

const CATEGORIES = [
  { key: "gps", label: "GPS OVERALL" },
  { key: "strength", label: "STRENGTH" },
  { key: "speed", label: "SPEED" },
  { key: "explosive", label: "EXPLOSIVE" },
  { key: "stamina", label: "STAMINA" },
  { key: "control", label: "CONTROL" },
];

// Column keys → row getters. `null` keys are non-sortable (e.g. Rank uses default order).
const COLUMNS = [
  { key: null,         label: "Rank",        get: (r) => r.rank },
  { key: "name",       label: "Athlete",     get: (r) => (r.name || "").toLowerCase() },
  { key: "gym",        label: "Institution", get: (r) => (r.gym || "").toLowerCase() },
  { key: "location",   label: "Location",    get: (r) => `${r.country || ""} ${r.city || ""}`.toLowerCase() },
  { key: "gender",     label: "Gender",      get: (r) => r.gender || "" },
  { key: "score",      label: "GPS",         get: (r, cat) => cat === "gps" ? (r.gps ?? 0) : (r.pillars?.[cat] ?? 0) },
  { key: "archetype",  label: "Archetype",   get: (r) => (r.archetype?.code || "") },
];

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gyms, setGyms] = useState([]);
  const [archetypes, setArchetypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({
    type: "open",
    category: "gps",
    country: "",
    gym: "",
    archetype: "",
  });

  // Load filter option lists once on mount — these don't depend on filters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/gyms?approved_only=true").then((r) => setGyms(r.data.gyms || [])).catch(() => {});
    api.get("/archetypes").then((r) => setArchetypes(r.data.archetypes || [])).catch(() => {});
    api.get("/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("limit", "100");
      try {
        const { data } = await api.get(`/leaderboard?${params.toString()}`);
        setRows(data.rows || []);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const dl = useDownloadEventLeaderboard();

  const exportTop10 = () => {
    const ranked = gender
      ? rows.filter((r) => matchesLeaderboardFilters(r, "", gender)).map((r, i) => ({ ...r, rank: i + 1 }))
      : rows;
    const pro = filters.type === "pro";
    const cat = CATEGORIES.find((c) => c.key === filters.category)?.label || "GPS";
    const meta = [new Date().toISOString().slice(0, 10), filters.country, filters.gym, filters.archetype].filter(Boolean);
    dl.download("main", {
      fetchFresh: false,
      snapshot: {
        event: { name: pro ? "Leaderboard Pro" : "Leaderboard" },
        entries: ranked.slice(0, 10).map((r) => ({ ...r, user_id: r.id })),
        gender,
        tag: `GLOBAL RANKINGS${pro ? " · VERIFIED" : " · OPEN"}`,
        meta,
        rankedBy: cat,
      },
    });
  };

  // Client-side sort overlay — default is server order (sorted by score desc).
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const toggleSort = (key) => {
    if (!key) return;
    setSort((s) => s.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: key === "name" || key === "gym" || key === "archetype" || key === "location" || key === "gender" ? "asc" : "desc" }
    );
  };
  const [query, setQuery] = useState("");
  const [gender, setGender] = useState("");
  const sortedRows = useMemo(() => {
    const ranked = gender
      ? rows.filter((r) => matchesLeaderboardFilters(r, "", gender)).map((r, i) => ({ ...r, rank: i + 1 }))
      : rows;
    const visible = ranked.filter((r) => matchesLeaderboardFilters(r, query, ""));
    if (!sort.key) return visible;
    const col = COLUMNS.find((c) => c.key === sort.key);
    if (!col) return visible;
    const out = visible.sort((a, b) => {
      const va = col.get(a, filters.category);
      const vb = col.get(b, filters.category);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, sort, filters.category, query, gender]);

  const renderSortIcon = (key) => {
    if (!key) return null;
    if (sort.key !== key) return <ArrowUpDown size={12} className="inline ml-1 opacity-40" />;
    return sort.dir === "asc"
      ? <ArrowUp size={12} className="inline ml-1 text-cyan-400" />
      : <ArrowDown size={12} className="inline ml-1 text-cyan-400" />;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="leaderboard-page">
      <div className="gps-tag mb-4">GLOBAL RANKINGS</div>
      <h1 className="gps-heading text-4xl md:text-6xl">
        {filters.type === "pro" ? "LEADERBOARD PRO" : "LEADERBOARD"}
      </h1>
      <p className="text-mute text-sm mt-3 max-w-2xl">
        {filters.type === "pro"
          ? "Admin-verified score logs only. Authenticated, audited, eligible for records."
          : "Every submitted GPS score, verified or pending review. The open arena."}
      </p>

      {/* Type tabs */}
      <div className="mt-8 flex gap-2 flex-wrap" data-testid="leaderboard-type-tabs">
        <button
          onClick={() => set("type", "open")}
          className={`gps-btn ${filters.type === "open" ? "" : "ghost"}`}
          data-testid="lb-tab-open"
        >
          LEADERBOARD
        </button>
        <button
          onClick={() => set("type", "pro")}
          className={`gps-btn ${filters.type === "pro" ? "" : "ghost"}`}
          data-testid="lb-tab-pro"
        >
          LEADERBOARD PRO
        </button>
        <button
          onClick={exportTop10}
          className="gps-btn ghost ml-auto inline-flex items-center gap-2"
          disabled={loading || dl.busyEventId === "main" || rows.length === 0}
          data-testid="lb-export-png"
          title="Download top 10 as PNG"
        >
          <Download size={14} /> EXPORT TOP 10
        </button>
      </div>

      <div className="mt-6">
        <LeaderboardSearchBar query={query} onQuery={setQuery} gender={gender} onGender={setGender} />
      </div>

      {/* Filters */}
      <GlassPanel className="mt-4 p-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="leaderboard-filters">
        <div>
          <label className="gps-label">CATEGORY</label>
          <select className="gps-input" value={filters.category} onChange={(e) => set("category", e.target.value)} data-testid="filter-category">
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="gps-label">COUNTRY</label>
          <select className="gps-input" value={filters.country} onChange={(e) => set("country", e.target.value)} data-testid="filter-country">
            <option value="">ALL COUNTRIES</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="gps-label">INSTITUTION</label>
          <select className="gps-input" value={filters.gym} onChange={(e) => set("gym", e.target.value)} data-testid="filter-gym">
            <option value="">ALL INSTITUTIONS</option>
            {gyms.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label className="gps-label">ARCHETYPE</label>
          <select className="gps-input" value={filters.archetype} onChange={(e) => set("archetype", e.target.value)} data-testid="filter-archetype">
            <option value="">ALL</option>
            {archetypes.map((a) => (
              <option key={a.code} value={a.code}>{`${a.code} · ${a.name}`}</option>
            ))}
          </select>
        </div>
      </GlassPanel>

      <GlassPanel className="mt-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px] border-collapse" data-testid="leaderboard-table">
          <thead>
            <tr className="border-b border-white/15">
              {COLUMNS.map((c) => (
                <th
                  key={c.label}
                  className={`text-left p-4 gps-heading text-xs tracking-[0.22em] text-mute font-medium uppercase select-none ${c.key ? "cursor-pointer hover:text-white" : ""}`}
                  onClick={() => toggleSort(c.key)}
                  data-testid={c.key ? `lb-sort-${c.key}` : undefined}
                  aria-sort={sort.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                >
                  {c.label}{renderSortIcon(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-white gps-mono text-xs">LOADING...</td></tr>
            )}
            {!loading && sortedRows.map((r) => {
              const podium = PODIUM[r.rank];
              return (
              <tr
                key={r.id}
                className={`border-b border-white/5 transition-colors ${podium ? `${podium.rowBg} ${podium.ring}` : "hover:bg-white/5"}`}
                data-testid={`lb-row-${r.rank}`}
                data-podium={podium ? podium.label.toLowerCase() : undefined}
              >
                <td className="p-4 gps-mono whitespace-nowrap">
                  <span className={podium?.accentText || "text-white"}>#{String(r.rank).padStart(2, "0")}</span>
                  {podium && <span className="ml-2 inline-block align-middle">{podium.icon}</span>}
                </td>
                <td className="p-4">
                  <span className={`gps-heading ${podium?.accentText || "text-white"}`}>{r.name}</span>
                </td>
                <td className="p-4 text-mute text-xs gps-mono">{r.gym || "—"}</td>
                <td className="p-4 text-mute text-xs gps-mono">{r.city ? `${r.city}, ` : ""}{r.country}</td>
                <td className="p-4 gps-mono text-xs text-white">{r.gender}</td>
                <td className="p-4 big-numeric text-white text-lg">
                  {filters.category === "gps" ? r.gps?.toFixed(1) : r.pillars?.[filters.category]?.toFixed(1) ?? "—"}
                </td>
                <td className="p-4 gps-mono text-xs">
                  <span className="text-white">{r.archetype?.code}</span>
                  <span className="text-mute ml-2">{r.archetype?.name}</span>
                </td>
              </tr>
              );
            })}
            {!loading && sortedRows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-mute gps-mono text-xs">NO ATHLETES MATCH FILTERS</td></tr>
            )}
          </tbody>
        </table>
      </GlassPanel>
      {dl.payload && (
        <div style={{ position: "fixed", top: -100000, left: -100000, pointerEvents: "none" }} aria-hidden="true">
          <EventLeaderboardShareCard ref={dl.nodeRef} {...dl.payload} />
        </div>
      )}
    </div>
  );
}
